import {
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	flip,
	offset,
	shift,
	size,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";

const PAGE_SIZES = [10, 20, 30, 40, 50];

function PageSizeSelector({
	total = 0,
	value = 10,
	onChange,
	disabled = false,
	startItem,
	endItem,
}) {
	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: "bottom-start",
		middleware: [
			offset(4),
			flip({ padding: 8 }),
			shift({ padding: 8 }),
			size({
				apply({ rects, elements, availableHeight }) {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`,
						maxHeight: `${availableHeight}px`,
					});
				},
				padding: 8,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context, { enabled: !disabled });
	const dismiss = useDismiss(context);
	const role = useRole(context, { role: "listbox" });

	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

	const visibleSizes = PAGE_SIZES.filter((size, idx) => {
		const prev = PAGE_SIZES[idx - 1] ?? 0;
		return prev < total;
	});

	const handleSelect = (size) => {
		if (disabled) return;
		onChange?.(size);
		setIsOpen(false);
	};

	return (
		<div className="flex items-center gap-3">
			{startItem != null && endItem != null && (
				<span className="text-blue-gray text-xs sm:text-sm">
					Showing {startItem} to {endItem} of {total} entries
				</span>
			)}

			{total > visibleSizes[0] && (
				<div className="relative w-12">
					<div
						ref={refs.setReference}
						className={`flex w-full items-center justify-between rounded-lg border border-gray-600 bg-blue-gray bg-opacity-20 p-2 text-xs ${
							disabled
								? "cursor-not-allowed text-gray-500 opacity-50"
								: "cursor-pointer text-gray-400"
						}`}
						{...getReferenceProps()}
					>
						<span>{value}</span>
						<FaChevronDown
							className={`ml-2 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""} ${
								disabled ? "opacity-50" : ""
							}`}
						/>
					</div>

					{isOpen && !disabled && (
						<FloatingPortal>
							<FloatingFocusManager context={context} initialFocus={-1}>
								<div
									ref={refs.setFloating}
									style={floatingStyles}
									className="thin-scrollbar z-50 overflow-y-auto rounded-lg border border-gray-600 bg-dropdown-bg"
									{...getFloatingProps()}
								>
									<ul className="text-start text-sm">
										{visibleSizes.map((size) => (
											<li
												key={size}
												onClick={() => handleSelect(size)}
												className={`cursor-pointer p-2 text-sm text-white hover:bg-dropdown-hover ${
													value === size ? "bg-blue-gray bg-opacity-80" : ""
												}`}
											>
												{size}
											</li>
										))}
									</ul>
								</div>
							</FloatingFocusManager>
						</FloatingPortal>
					)}
				</div>
			)}
		</div>
	);
}

export default PageSizeSelector;
